import { useCallback, useEffect, useRef } from 'react';

import { EDUCATION_TC_FLAT_ITEMS, MENU_REGISTRY, type NavigationItem, type SideNavNavigationItem, useMenuLabels, useNavigation } from 'components/navigation/navigation_wrapper';
import { PartnerGMetrixTableDialog, PartnerGMetrixTableDialogHandle } from 'components/PartnerGMetrixTableDialog/PartnerGMetrixTableDialog';
import { PartnerSelect } from 'components/navigation/side_nav/components/PartnerSelect';
import { ConditionalWrapper } from 'components/conditional_wrapper/ConditionalWrapper';
import { SideNavItem } from 'components/navigation/side_nav/components/SideNavItem';
import { SideNavContext } from 'components/navigation/side_nav/context';
import { SIDENAV_CONFIG } from 'components/navigation/side_nav/config';
import { useEscape, useSuperState, useWindowDimensions } from 'hooks';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useNavDrawer } from 'components/navigation/nav_drawer';
import { Image, Nav } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import { useApp } from 'app';
import cn from 'classnames';

import 'components/navigation/side_nav/styles/SideNav.scss';

const LOGO_URL = 'https://content.gmetrix.net/images/GMetrix/GMetrix-Logo-Reverse.png';
const ICON_LOGO_URL = '/favicon.png';
const EDUCATION_SECTORS = [1, 4];
const TC_ROLE_ID = 4;

// The Testing Centers "View inactive/expiring" item opens the inactive TCs report (id 11),
// which is distributor-scoped: the user must pick a distributor (ScopeId 2) so the report
// URL carries the correct scope object.
const DISTRIBUTOR_SCOPE_ID = 2;
const INACTIVE_TC_REPORT_ITEM_ID = 'lbMenuMpTCViewExp';
const INACTIVE_TC_REPORT_ID = 11;

export type SideNavContextValue = {
    expandedItemId: string;
    getActiveChildId: (item: NavigationItem) => string;
    handleNavigate: (e: any, item: NavigationItem) => void;
    handleToggleExpand:(itemId: string) => void;
    isCollapsed: boolean;
    isItemActive:(item: NavigationItem) => boolean;
    items: NavigationItem[];
}

export const SideNav = () => {

    const [state, setState] = useSuperState({
        expandedItemId: '' as string,
        isHovered: false,
        items: [] as NavigationItem[]
    });
    const { expandedItemId, items } = state;

    const { isFeatureEnabled, selectedPartner } = useApp();
    const { allowedItems } = useNavigation();
    const labels = useMenuLabels();
    const { pathname: currentPath, search: currentSearch } = useLocation();
    const { formatMessage } = useIntl();

    // Below COMPACT (<1200px) the rail collapses unless hovered;
    // isMobile (<576px) switches to the off-canvas drawer. Derived from `width` (not the hook's `isBelow` flag) 
    // so it is correct on the very first render — the sidenav remounts on every navigation, and a one-tick-late flag 
    // would briefly mount the rail in the wrong mode and visibly shift the layout.
    const { width: viewportWidth } = useWindowDimensions();
    const isCompact = viewportWidth < SIDENAV_CONFIG.COMPACT;
    const { close, isMobile, isOpen } = useNavDrawer();
    const navigate = useNavigate();

    const sidebarRef = useRef<HTMLDivElement>(null);
    const distributorPickerRef = useRef<PartnerGMetrixTableDialogHandle>(null);

    // On mobile the nav is always shown fully expanded inside the drawer. Otherwise
    // below COMPACT it collapses to the rail unless the user is hovering/focusing it.
    const isCollapsed = isCompact && !state.isHovered && !isMobile;

    const resolveMenu = useCallback(() => {

        const allowedSet = new Set(allowedItems);
        const roleId = selectedPartner?.type ?? 0;
        const sector = selectedPartner?.sector ?? 0;

        const isEducation = EDUCATION_SECTORS.includes(sector);
        const isEducationTC = isEducation && roleId === TC_ROLE_ID;

        // For education TCs, swap the Students submenu for flat items
        let registry = MENU_REGISTRY;
        if (isEducationTC) {

            registry = [
                ...registry.filter(item => item.id !== 'ddlStud'),
                ...EDUCATION_TC_FLAT_ITEMS
            ];

        }

        const filterAndMap = (items: SideNavNavigationItem[]): NavigationItem[] => items
            .filter(item => {

                // Role filter
                if (item.roles && !item.roles.includes(roleId)) return false;

                // Feature flag filter
                if (item.featureFlag && !isFeatureEnabled(item.featureFlag)) return false;

                // Sector filter
                if (item.sectorFilter === 'education' && !isEducation) return false;

                // Permission filter (server-side allowed list)
                if (!allowedSet.has(item.id)) return false;

                return true;

            })
            .map(item => ({
                id: item.id,
                children: item.children ? filterAndMap(item.children) : [],
                icon: item.icon ?? '',
                iconLink: item.iconLink ?? '',
                label: labels[item.labelKey ?? item.id],
                linkTarget: item.linkTarget ?? '',
                path: item.path ?? ''
            }));

        return filterAndMap(registry);

    }, [allowedItems, isFeatureEnabled, labels, selectedPartner?.sector, selectedPartner?.type]);

    const handleMouseEnter = useCallback(() => {

        if (isCompact && !isMobile) {

            setState({ isHovered: true });

        }

    }, [isCompact, isMobile]);

    const handleMouseLeave = useCallback(() => {

        setState({ isHovered: false });

    }, []);

    const handleFocus = useCallback(() => {

        if (isCompact && !isMobile) {

            setState({ isHovered: true });

        }

    }, [isCompact, isMobile]);

    const handleBlur = useCallback((e: any) => {

        // Only collapse when focus leaves the sidenav entirely.
        if (!e.currentTarget.contains(e.relatedTarget)) {

            setState({ isHovered: false });

        }

    }, []);

    const normalizePath = (path: string) => path.toLowerCase().replace('.aspx', '').split('?')[0];

    const flattenPaths = (itemsToFlatten: NavigationItem[]): string[] => itemsToFlatten.flatMap(item => {

        const ownPath = item.path ? [item.path] : [];
        const childPaths = item.children.length > 0 ? flattenPaths(item.children) : [];
        return [...ownPath, ...childPaths];

    });

    const hasMoreSpecificMatchingPath = (path: string) => {

        const normalized = normalizePath(path);
        const current = normalizePath(currentPath);
        const allPaths = flattenPaths(items);

        return allPaths.some(candidate => {

            if (candidate === path) return false;
            if (!matchesPath(candidate)) return false;

            const candidateNormalized = normalizePath(candidate);

            return candidateNormalized.length > normalized.length
                && candidateNormalized.startsWith(`${normalized}/`)
                && current.startsWith(`${normalized}/`);

        });

    };

    // True when some menu item carries query params that all match the current URL
    // (and shares the current path). Lets a query-less item defer to a query-specific
    // sibling only when one actually matches — otherwise the base item stays active
    // for detail views that append their own params (e.g. /licenses/search?licenseId=5).
    const hasMatchingQueryParamItem = () => {

        const current = normalizePath(currentPath);
        const currentParams = new URLSearchParams(currentSearch.toLowerCase());

        return flattenPaths(items).some(candidate => {

            const [candidatePath, candidateQuery] = candidate.toLowerCase().replace('.aspx', '').split('?');

            if (!candidateQuery || candidatePath !== current) return false;

            return [...new URLSearchParams(candidateQuery).entries()]
                .every(([key, value]) => currentParams.get(key) === value);

        });

    };

    const matchesPath = (path: string) => {

        const current = normalizePath(currentPath);
        const currentParams = new URLSearchParams(currentSearch.toLowerCase());

        const [pathPart, queryPart] = path.toLowerCase().replace('.aspx', '').split('?');
        const normalized = pathPart;

        // Exact match for root path to avoid matching everything
        if (normalized === '/') return current === '/';

        if (!current.startsWith(normalized)) return false;

        // Ensure match ends at a segment boundary (exact match or followed by '/')
        if (current.length > normalized.length && current[normalized.length] !== '/') return false;

        // When the menu item defines query params, require them to match the current URL
        if (queryPart) {

            const itemParams = new URLSearchParams(queryPart);
            for (const [key, value] of itemParams.entries()) {

                if (currentParams.get(key) !== value) return false;

            }

        } else if (current === normalized && currentSearch && hasMatchingQueryParamItem()) {

            // Menu item has no query params but the current URL does, and a more specific
            // sibling item with matching query params exists let that item take precedence.
            // When no such sibling matches, this base item should still match (e.g. a detail
            // view that appends its own params like /licenses/search?licenseId=5).
            return false;

        }

        return true;

    };

    const isItemActive = (item: NavigationItem): boolean => {

        if (item.path && matchesPath(item.path) && !hasMoreSpecificMatchingPath(item.path)) return true;

        return item.children.some(child => child.path && matchesPath(child.path));

    };

    const getActiveChildId = (item: NavigationItem): string => {

        const matchingChildren = item.children.filter(child => child.path && matchesPath(child.path));

        // Prefer the most specific (longest path) match
        const activeChild = matchingChildren.sort((a, b) => b.path.length - a.path.length)[0];

        return activeChild?.id ?? '';

    };

    // Prompt the user to pick a distributor, then open the inactive TCs report scoped to it.
    const promptDistributorThenViewInactiveTcs = async () => {

        const result = await distributorPickerRef.current?.show({
            dialogCustomTitle: formatMessage({ id: 'select_a_distributor.a1002c0e', defaultMessage: 'Select a Distributor' }),
            multiselect: false,
            option: 'Change',
            scopeId: DISTRIBUTOR_SCOPE_ID
        });
        if (!result) return;

        const chosen = result.partners.find(p => p.ID === result.ids[0]);
        if (!chosen) return;

        const params = new URLSearchParams({
            ScopeId: String(DISTRIBUTOR_SCOPE_ID),
            ScopeObjectId: String(chosen.ID),
            ScopeObjectName: chosen.Name
        });
        navigate(`/reports/${INACTIVE_TC_REPORT_ID}?${params.toString()}`);

    };

    const handleNavigate = (e, item: NavigationItem) => {

        // This item needs a distributor selected before it can build a valid report URL,
        // so intercept the click and open the picker instead of following the link.
        if (item.id === INACTIVE_TC_REPORT_ITEM_ID) {

            e.preventDefault();
            e.stopPropagation();
            promptDistributorThenViewInactiveTcs();
            return;

        }

        const { linkTarget: target, path } = item;

        switch (target){

            case 'blank':
                e.preventDefault();
                window.open(path, '_blank');
                break;
            case 'self':
                e.preventDefault();
                e.stopPropagation();
                window.location.href = path;
                break;

        }

        // Close the mobile drawer when a leaf item is activated.
        if (isMobile && item.children.length === 0) close();

    };

    const handleToggleExpand = (itemId: string) => setState({
        expandedItemId: state.expandedItemId === itemId ? '' : itemId
    });

    const filterAllowedItems = useCallback(() => {

        const resolved = resolveMenu();
        const activeParent = resolved.find(item => item.children.length > 0 && isItemActive(item));

        setState({
            expandedItemId: activeParent?.id ?? '',
            items: resolved
        });

    }, [allowedItems, isFeatureEnabled, labels, selectedPartner?.sector, selectedPartner?.type]);

    // Close the mobile drawer on navigation (route change) and via the Escape key.
    useEffect(() => {

        if (isMobile && isOpen) {

            close();

        }

    }, [currentPath]); // eslint-disable-line react-hooks/exhaustive-deps

    useEscape({ action: close });

    useEffect(() => {

        filterAllowedItems();

    }, [filterAllowedItems]);

    // Collapse any open submenu when sidebar collapses
    useEffect(() => {

        if (isCollapsed) {

            setState({ expandedItemId: '' });

        }

    }, [isCollapsed]);

    const width = isCollapsed ? SIDENAV_CONFIG.COLLAPSED : SIDENAV_CONFIG.EXPANDED;

    const context: SideNavContextValue = {
        expandedItemId,
        getActiveChildId,
        handleNavigate,
        handleToggleExpand,
        isCollapsed,
        isItemActive,
        items
    };

    const navClassname = cn(
        'side-nav bg-navy flex-column flex-nowrap d-print-none text-white overflow-hidden h-100 p-2 gap-2',
        {

            // On mobile the nav becomes an off-canvas drawer that slides in/out.
            'side-nav--drawer': isMobile,

            'side-nav--drawer-open': isMobile && isOpen,

            // When compact (but not mobile), the nav floats over the content so
            // hover-expanding it never reflows the page.
            'side-nav--floating': isCompact && !isMobile
        }
    );

    return (
        <SideNavContext.Provider value={context}>
            <ConditionalWrapper
                wrapper={children => (
                    <>
                        {children}
                        {isOpen && <div aria-hidden="true" className="side-nav-backdrop" onClick={close} />}
                    </>
                )}
                condition={isMobile}
            >
                <div className="side-nav-rail h-100 flex-shrink-0">
                    <Nav
                        aria-hidden={isMobile && !isOpen}
                        aria-label={formatMessage({ id: 'main_navigation.bfc13b6e', defaultMessage: 'Main navigation' })}
                        className={navClassname}
                        id="side-nav-drawer"
                        onBlur={handleBlur}
                        onFocus={handleFocus}
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                        ref={sidebarRef}
                        role="navigation"
                        style={{ width }}
                    >
                        {/* Logo — full wordmark when expanded, "G" mark when collapsed.
                            aspectRatio reserves the image's height before it loads so a
                            cold-cache navigation doesn't shift the layout. */}
                        <Link className="d-flex align-items-center justify-content-center flex-shrink-0 py-3" to="/">
                            <Image
                                style={{
                                    aspectRatio: isCollapsed ? '1 / 1' : '1200 / 143',
                                    width: isCollapsed ? 32 : '75%'
                                }}
                                alt={formatMessage({ id: 'gmetrix_logo.ad231e6e', defaultMessage: 'GMetrix logo' })}
                                src={isCollapsed ? ICON_LOGO_URL : LOGO_URL}
                            />
                        </Link>

                        {/* Partner selector — hidden while collapsed */}
                        {!isCollapsed && <PartnerSelect />}

                        {/* Menu Items — scrollable region */}
                        <div className="side-nav-menu-scroll d-flex flex-column gap-1 flex-grow-1 overflow-y-auto overflow-x-hidden" style={{ minHeight: 0 }}>
                            {items.map(item => (
                                <SideNavItem
                                    activeChildId={getActiveChildId(item)}
                                    isActive={isItemActive(item)}
                                    isExpanded={state.expandedItemId === item.id}
                                    item={item}
                                    key={item.id}
                                />
                            ))}
                        </div>

                    </Nav>
                </div>
            </ConditionalWrapper>

            <PartnerGMetrixTableDialog
                multiselect={false}
                option="Change"
                ref={distributorPickerRef}
                scopeId={DISTRIBUTOR_SCOPE_ID}
            />
        </SideNavContext.Provider>

    );

};